<script setup lang="ts">
import { useDescope } from '@descope/vue-sdk';
import { Loader2 } from 'lucide-vue-next';
import { computed, onMounted, ref, watch } from 'vue';

import { MCPConnectorConfig } from '@/components/settingsMenu/sections/mcp-connector-ui/mcpConnectorConfig';
import MCPConnectorItem from '@/components/settingsMenu/sections/mcp-connector-ui/MCPConnectorItem.vue';
import MCPDisconnectDialog from '@/components/settingsMenu/sections/mcp-connector-ui/MCPDisconnectDialog.vue';
import MCPToolPermissions from '@/components/settingsMenu/sections/mcp-connector-ui/MCPToolPermissions.vue';
import { Separator } from '@/components/ui/separator';
import { useToast } from '@/components/ui/toast/use-toast';
import {
    ConnectionState,
    type ConnectorState,
    disconnectMCPConnector,
    listMCPConnectors
} from '@/services/mcpConnectors';
import { useTenantStore } from '@/stores/tenant';

const tenantStore = useTenantStore();
const { outbound } = useDescope();
const toast = useToast();

const mcpConnectors = ref<ConnectorState[]>([]);
const isMCPConnectorsLoading = ref<boolean>(true);

const mcpConnectorsAvailable = computed(() => {
    return mcpConnectors.value.filter((connector) =>
        Object.keys(MCPConnectorConfig).includes(connector.provider)
    );
});

// Guards a double click: the connect path navigates away, so a second call would
// race the redirect.
const pendingProvider = ref<string | null>(null);
const isRemoveAccessDialogOpen = ref(false);
const connectorToDisconnect = ref<ConnectorState | null>(null);
/** One expanded connector at a time: the tool list is long enough to be the page. */
const expandedProvider = ref<string | null>(null);

const toggleExpanded = (provider: string) => {
    expandedProvider.value =
        expandedProvider.value === provider ? null : provider;
};

async function getMCPConnectors() {
    const tenantId = tenantStore.tenantId;

    isMCPConnectorsLoading.value = true;
    mcpConnectors.value = [];

    if (!tenantId) {
        isMCPConnectorsLoading.value = false;
        return;
    }

    try {
        const connectors = await listMCPConnectors(tenantId);
        // A tenant switch during the fetch already started a fresh load.
        if (tenantId !== tenantStore.tenantId) return;
        mcpConnectors.value = connectors;
    } catch (error) {
        if (tenantId !== tenantStore.tenantId) return;
        toast.toast({
            title: 'Failed to get MCP connectors list',
            description:
                'An error occurred while getting the MCP connectors list. Please try again.',
            variant: 'destructive'
        });
        console.error(error);
    } finally {
        if (tenantId === tenantStore.tenantId) {
            isMCPConnectorsLoading.value = false;
        }
    }
}

const handleMCPConnectorConnection = async (connector: ConnectorState) => {
    if (
        isMCPConnectorsLoading.value ||
        pendingProvider.value ||
        !tenantStore.tenantId
    ) {
        return;
    }

    if (connector.state === ConnectionState.CONNECTED) {
        connectorToDisconnect.value = connector;
        isRemoveAccessDialogOpen.value = true;
        return;
    }

    pendingProvider.value = connector.provider;
    try {
        // Covers NOT_CONNECTED and NEEDS_RECONNECT: both go back through consent.
        await connectMCP(tenantStore.tenantId, connector);
    } finally {
        pendingProvider.value = null;
    }
};

const confirmRemoveAccess = async () => {
    const connector = connectorToDisconnect.value;
    if (!connector || !tenantStore.tenantId) {
        return;
    }

    pendingProvider.value = connector.provider;
    try {
        await disconnectMCP(tenantStore.tenantId, connector.provider);
    } finally {
        pendingProvider.value = null;
        connectorToDisconnect.value = null;
    }
};

const disconnectMCP = async (tenantId: string, provider: string) => {
    try {
        await disconnectMCPConnector(tenantId, provider);
        if (expandedProvider.value === provider) expandedProvider.value = null;
        mcpConnectors.value = mcpConnectors.value.map((c) =>
            c.provider === provider
                ? { ...c, state: ConnectionState.NOT_CONNECTED }
                : c
        );
    } catch (error) {
        toast.toast({
            title: 'Failed to disconnect connector',
            description:
                'An error occurred while disconnecting the connector. Please try again.',
            variant: 'destructive'
        });
        console.error(error);
    }
};

const connectMCP = async (tenantId: string, connector: ConnectorState) => {
    const failed = (cause: unknown) => {
        toast.toast({
            title: 'Failed to connect connector',
            description:
                'An error occurred while connecting the connector. Please try again.',
            variant: 'destructive'
        });
        console.error(cause);
    };

    try {
        const redirectUrl = new URL(window.location.href);
        redirectUrl.searchParams.set('settingsTab', 'connections');

        const response = await outbound.connect(connector.descope_app_id, {
            tenantId: tenantId,
            redirectUrl: redirectUrl.toString()
        });

        if (!response.ok || !response.data?.url) {
            failed(response.error ?? 'outbound.connect returned no url');
            return;
        }

        window.location.replace(response.data.url);
    } catch (error) {
        failed(error);
    }
};

onMounted(() => {
    getMCPConnectors();
});

watch(
    () => tenantStore.tenantId,
    () => {
        getMCPConnectors();
    }
);
</script>

<template>
    <section class="mcp-connectors-connection rounded-xl border p-5 space-y-4">
        <h3 class="text-md font-semibold">MCP connectors connection</h3>
        <div
            v-if="isMCPConnectorsLoading"
            class="text-sm text-muted-foreground"
        >
            <Loader2 class="h-4 w-4 animate-spin" />
        </div>

        <div
            v-else-if="!tenantStore.tenantId"
            class="text-sm text-muted-foreground"
        >
            Select a workspace first to configure MCP connectors.
        </div>

        <div
            v-else-if="mcpConnectorsAvailable.length === 0"
            class="text-sm text-muted-foreground"
        >
            No MCP connectors available.
        </div>

        <template
            v-else
            v-for="(connector, index) in mcpConnectorsAvailable"
            :key="connector.provider"
        >
            <MCPConnectorItem
                :connector="connector"
                :is-pending="pendingProvider === connector.provider"
                :is-expandable="connector.state === ConnectionState.CONNECTED"
                :is-expanded="expandedProvider === connector.provider"
                @action="handleMCPConnectorConnection(connector)"
                @toggle="toggleExpanded(connector.provider)"
            />

            <!-- Indented to the connector name, so the panel reads as belonging to
                 the row above rather than to the section. -->
            <MCPToolPermissions
                v-if="
                    expandedProvider === connector.provider &&
                    tenantStore.tenantId
                "
                class="border-l pl-4 ml-4"
                :tenant-id="tenantStore.tenantId"
                :provider="connector.provider"
            />

            <Separator v-if="index < mcpConnectorsAvailable.length - 1" />
        </template>
    </section>

    <MCPDisconnectDialog
        v-model:is-confirm-dialog-open="isRemoveAccessDialogOpen"
        :connector="connectorToDisconnect"
        @confirm="confirmRemoveAccess"
    />
</template>
